import { useEffect } from "react";

const SITE_NAME = "Highend Holidays Homes";
const SITE_URL = "https://highendholidays.ae";

const absUrl = (maybeRelative) => {
  if (!maybeRelative) return "";
  if (maybeRelative.startsWith("http")) return maybeRelative;
  if (maybeRelative.startsWith("/")) return `${SITE_URL}${maybeRelative}`;
  return `${SITE_URL}/${maybeRelative}`;
};

export default function useSEO({
  title,
  description,
  image,
  url,
  keywords,

  // Apartment/Property SEO extras (dynamic)
  city = "Dubai",
  country = "AE",
  rating,
  reviews,
  amenities = [],
  price, // AED numeric if available
}) {
  useEffect(() => {
    const pageUrl = url || SITE_URL;
    const ogImage = absUrl(image);

    // ---------- TITLE (SEO FORMAT) ----------
    // Example: "Premium Apartment with Burj Views in Dubai | Highend Holidays"
    if (title) {
     document.title = title
    ? `${title} | Luxury Holiday Apartment in ${city} | ${SITE_NAME}`
    : `${SITE_NAME} | Luxury Holiday Homes in Dubai`;

    } else {
      document.title = `${SITE_NAME} | Luxury Holiday Homes in Dubai`;
    }

    // ---------- META DESCRIPTION ----------
    if (description) {
      let meta = document.querySelector("meta[name='description']");
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.appendChild(meta);
      }
      const clean = description.replace(/\s+/g, " ").trim();
      meta.content =
        clean.length > 155 ? clean.slice(0, 152) + "..." : clean;
    }

    // ---------- META KEYWORDS (OPTIONAL) ----------
    if (keywords) {
      let meta = document.querySelector("meta[name='keywords']");
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "keywords";
        document.head.appendChild(meta);
      }
      meta.content = keywords;
    }

    // ---------- CANONICAL ----------
    if (pageUrl) {
      let link = document.querySelector("link[rel='canonical']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = pageUrl;
    }

    // ---------- OPEN GRAPH ----------
    const setOG = (property, content) => {
      if (!content) return;
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("property", property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    setOG("og:title", title ? `${title} in ${city}` : SITE_NAME);
    setOG(
      "og:description",
      description || "Luxury holiday apartments in Dubai with premium amenities."
    );
    setOG("og:image", ogImage);
    setOG("og:url", pageUrl);
    setOG("og:type", "website");
    setOG("og:site_name", SITE_NAME);

    // ---------- TWITTER ----------
    const setTwitter = (name, content) => {
      if (!content) return;
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    setTwitter("twitter:card", "summary_large_image");
    setTwitter("twitter:title", title);
    setTwitter("twitter:description", description);
    setTwitter("twitter:image", ogImage);

    // ---------- JSON-LD: LODGING BUSINESS ----------
    const apartmentSchemaId = "apartment-schema";
    let script = document.getElementById(apartmentSchemaId);
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = apartmentSchemaId;
      document.head.appendChild(script);
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LodgingBusiness",
      name: title,
      description,
      image: ogImage ? [ogImage] : undefined,
      url: pageUrl,
      address: {
        "@type": "PostalAddress",
        addressLocality: city,
        addressCountry: country,
      },
      aggregateRating:
        typeof rating === "number" && typeof reviews === "number"
          ? {
              "@type": "AggregateRating",
              ratingValue: rating,
              reviewCount: reviews,
            }
          : undefined,
      amenityFeature: Array.isArray(amenities)
        ? amenities.map((a) => ({
            "@type": "LocationFeatureSpecification",
            name: a,
          }))
        : undefined,
      offers: typeof price === "number"
        ? {
            "@type": "Offer",
            priceCurrency: "AED",
            price,
            availability: "https://schema.org/InStock",
            url: pageUrl,
          }
        : undefined,
    };

    // Remove undefined keys so JSON-LD is clean
    script.textContent = JSON.stringify(schema, (k, v) =>
      v === undefined ? undefined : v
    );

    // ---------- BREADCRUMB SCHEMA ----------
    const breadcrumbId = "breadcrumb-schema";
    let breadcrumbScript = document.getElementById(breadcrumbId);
    if (!breadcrumbScript) {
      breadcrumbScript = document.createElement("script");
      breadcrumbScript.type = "application/ld+json";
      breadcrumbScript.id = breadcrumbId;
      document.head.appendChild(breadcrumbScript);
    }

    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        {
          "@type": "ListItem",
          position: 2,
          name: "Apartments",
          item: `${SITE_URL}/apartments`,
        },
        { "@type": "ListItem", position: 3, name: title, item: pageUrl },
      ],
    });

    return () => {
      document.getElementById(apartmentSchemaId)?.remove();
      document.getElementById(breadcrumbId)?.remove();
    };
  }, [title, description, image, url, keywords, city, country, rating, reviews, amenities, price]);
}
